import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Badge, Table, Nav, Tab, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FiCloudRain, FiPlay, FiExternalLink, FiMail, FiRefreshCw, FiMessageSquare, FiTarget, FiLayers, FiUploadCloud, FiBell, FiTrash2 } from 'react-icons/fi';
import {
    backupTripsScheduledForecast,
    backupStandardCheckForecasts,
    getAllPartners,
    sendBackupForecastTriggeredBulkEmail,
    sendBackupForecastTriggeredBulkSms,
    sendStandardForecastTriggeredBackupEmails,
    sendStandardForecastNotTriggeredBackupEmails,
} from '../../services/api';
import { formatRequestError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { useLongRunningProgress, getLongWaitMessage } from '../../hooks/useLongRunningProgress';
import LongRunningOperationBanner from '../../components/Admin/LongRunningOperationBanner';
import EcmwfV2ExcelImportTab from '../../components/Admin/EcmwfV2ExcelImportTab';
import StandardScheduledForecastExcelImportTab from '../../components/Admin/StandardScheduledForecastExcelImportTab';
import CheckForecastBulkDeletePanel from '../../components/Admin/CheckForecastBulkDeletePanel';
import { canAccessCheckForecastBulkDelete, resolveAdminRole } from '../../utils/checkForecastBulkDelete';
import { normalizeAdminRole } from '../../utils/ecmwfExcelImport';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsForecastBackupCheck.css';

const CHECK_CLAIM_HIDDEN_FIELDS = new Set([
    'check_claim_id',
    'forecast_id',
    'observation_id',
]);

const CHECK_CLAIM_FIELD_ORDER = [
    'forecast_checked',
    'observation_checked',
    'is_triggered_forecast',
    'precipitation_is_triggered_forecast',
    'temperature_is_triggered_forecast',
    'wind_is_triggered_forecast',
    'count_hours_triggered',
    'precipitation_count_hours_triggered',
    'temperature_count_hours_triggered',
    'wind_count_hours_triggered',
    'trigger_number_hours',
    'prcp_trigger_hour_inf',
    'trigger_hour_sup',
    'trigger_low',
    'trigger_sup',
    'prcp_is_cumulated',
    'is_triggered_observation',
    'is_triggered_obs_meteofrance',
    'is_triggered_obs_meteomatics',
    'is_triggered_obs_meteostat',
    'check_claim_last_modified_by',
    'check_claim_last_modified_at',
];

const MAIN_TAB_KEYS = {
    NOTIFICATION: 'notification',
    BACKUP_TOOLS: 'backupTools',
    CHECK_FORECAST_DELETE: 'checkForecastDelete',
};

const FORECAST_FAMILY_KEYS = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const TAB_KEYS = {
    PARAMETERS: 'parameters',
    BULK: 'bulk',
    STANDARD_PARAMETERS: 'standardParameters',
    STANDARD_BULK: 'standardBulk',
    BULK_EMAIL: 'bulkEmail',
    BULK_SMS: 'bulkSms',
    STANDARD_BULK_EMAIL: 'standardBulkEmail',
    STANDARD_BULK_NOT_TRIGGERED_EMAIL: 'standardBulkNotTriggeredEmail',
    STANDARD_BULK_SMS: 'standardBulkSms',
    STANDARD_IMPORT_EXCEL: 'standardImportExcel',
    IMPORT_EXCEL: 'importExcel',
};

const POLICY_BACKUP_TAB_KEYS = new Set([TAB_KEYS.PARAMETERS, TAB_KEYS.STANDARD_PARAMETERS]);
const STANDARD_BACKUP_TAB_KEYS = new Set([TAB_KEYS.STANDARD_PARAMETERS, TAB_KEYS.STANDARD_BULK]);
const STANDARD_NOTIFY_EMAIL_TAB_KEYS = new Set([
    TAB_KEYS.STANDARD_BULK_EMAIL,
    TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL,
]);

const getStandardNotifyEmailI18nKey = (tabKey) => (
    tabKey === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL
        ? 'standardBulkNotTriggeredEmail'
        : 'standardBulkEmail'
);
const STANDARD_NOTIFY_ROLES = new Set([
    'admin',
    'administrator',
    'administrateur',
    'operations_limited',
    'restricted_admin',
]);
const STANDARD_BACKUP_DATA_SOURCES = ['ECMWF', 'METEOMATICS'];
const DEFAULT_STANDARD_DATA_SOURCE = 'ECMWF';

const DELETE_TAB_KEYS = {
    BY_DATE: 'deleteByDate',
    BY_DATE_POLICY: 'deleteByDatePolicy',
    BY_FILE: 'deleteByFile',
};

const formatDisplayValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') {
        return (
            <Badge bg={value ? 'success' : 'secondary'}>
                {value ? 'Oui' : 'Non'}
            </Badge>
        );
    }
    return String(value);
};

const isCheckInfosPayload = (checkClaims) => (
    Boolean(checkClaims && typeof checkClaims === 'object' && Array.isArray(checkClaims.check_infos))
);

const getStandardCheckInfos = (result) => {
    const infos = result?.check_claims?.check_infos;
    return Array.isArray(infos) ? infos : [];
};

const isNonEmptyObject = (value) => (
    Boolean(value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0)
);

const getNestedCheckClaim = (info) => {
    const nested = info?.check_claims;
    if (!nested || typeof nested !== 'object' || Array.isArray(nested) || Array.isArray(nested.check_infos)) {
        return {};
    }
    return nested;
};

const formatClientName = (client) => {
    const name = [client?.first_name, client?.last_name]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
    return name || '—';
};

const formatProcessingTime = (ms) => {
    if (ms == null || ms === '') return null;
    const n = Number(ms);
    if (Number.isNaN(n)) return String(ms);
    return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;
};

const formatRecipients = (recipients) => {
    if (Array.isArray(recipients)) return recipients.filter(Boolean).join(', ') || '—';
    if (recipients == null || recipients === '') return '—';
    return String(recipients);
};

const getStandardNotifyEmailPayload = (result) => {
    if (result?.not_triggered_emails && typeof result.not_triggered_emails === 'object') {
        return result.not_triggered_emails;
    }
    if (result?.trigger_emails && typeof result.trigger_emails === 'object') {
        return result.trigger_emails;
    }
    return {};
};

const buildCheckClaimRows = (checkClaims) => {
    if (!checkClaims || typeof checkClaims !== 'object' || isCheckInfosPayload(checkClaims)) return [];

    const orderedKeys = [
        ...CHECK_CLAIM_FIELD_ORDER.filter((key) => key in checkClaims),
        ...Object.keys(checkClaims).filter(
            (key) => !CHECK_CLAIM_FIELD_ORDER.includes(key) && !CHECK_CLAIM_HIDDEN_FIELDS.has(key)
        ),
    ];

    return orderedKeys.map((key) => ({
        key,
        value: checkClaims[key],
    }));
};

const emptyTabState = () => ({
    runDate: '',
    policyCode: '',
    dataSource: DEFAULT_STANDARD_DATA_SOURCE,
    partnerNames: [],
    partnerSearch: '',
    loading: false,
    error: null,
    result: null,
});

const AdminTripsForecastBackupCheck = () => {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const canBulkDeleteCheckForecast = canAccessCheckForecastBulkDelete(resolveAdminRole(accessToken));
    const [mainTab, setMainTab] = useState(MAIN_TAB_KEYS.BACKUP_TOOLS);
    const [notifySubTab, setNotifySubTab] = useState(TAB_KEYS.BULK_EMAIL);
    const [notifyStandardSubTab, setNotifyStandardSubTab] = useState(TAB_KEYS.STANDARD_BULK_EMAIL);
    const [notifyFamily, setNotifyFamily] = useState(FORECAST_FAMILY_KEYS.STANDARD);
    const [forecastFamily, setForecastFamily] = useState(FORECAST_FAMILY_KEYS.STANDARD);
    const [backupSubTab, setBackupSubTab] = useState(TAB_KEYS.PARAMETERS);
    const [backupStandardSubTab, setBackupStandardSubTab] = useState(TAB_KEYS.STANDARD_PARAMETERS);
    const [deleteSubTab, setDeleteSubTab] = useState(DELETE_TAB_KEYS.BY_DATE);
    const activeTab = (
        mainTab === MAIN_TAB_KEYS.NOTIFICATION
            ? (notifyFamily === FORECAST_FAMILY_KEYS.STANDARD ? notifyStandardSubTab : notifySubTab)
            : mainTab === MAIN_TAB_KEYS.CHECK_FORECAST_DELETE
                ? deleteSubTab
                : mainTab === MAIN_TAB_KEYS.BACKUP_TOOLS && forecastFamily === FORECAST_FAMILY_KEYS.STANDARD
                    ? backupStandardSubTab
                    : backupSubTab
    );
    const [tabState, setTabState] = useState({
        [TAB_KEYS.PARAMETERS]: emptyTabState(),
        [TAB_KEYS.BULK]: emptyTabState(),
        [TAB_KEYS.STANDARD_PARAMETERS]: emptyTabState(),
        [TAB_KEYS.STANDARD_BULK]: emptyTabState(),
        [TAB_KEYS.BULK_EMAIL]: emptyTabState(),
        [TAB_KEYS.BULK_SMS]: emptyTabState(),
        [TAB_KEYS.STANDARD_BULK_EMAIL]: emptyTabState(),
        [TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL]: emptyTabState(),
    });
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [showEmailConfirm, setShowEmailConfirm] = useState(false);
    const [showStandardEmailConfirm, setShowStandardEmailConfirm] = useState(false);
    const [showStandardNotTriggeredEmailConfirm, setShowStandardNotTriggeredEmailConfirm] = useState(false);
    const [showSmsConfirm, setShowSmsConfirm] = useState(false);
    const [standardInfosFilter, setStandardInfosFilter] = useState('all');

    const prefix = 'pages.adminTripsForecastBackupCheck';
    const canRunStandardBackupEmail = STANDARD_NOTIFY_ROLES.has(
        normalizeAdminRole(resolveAdminRole(accessToken)),
    );
    const isStandardNotifySmsPlaceholder = activeTab === TAB_KEYS.STANDARD_BULK_SMS;
    const current = tabState[activeTab] ?? emptyTabState();
    const isAnyTabLoading = useMemo(
        () => Object.values(tabState).some((tab) => tab?.loading),
        [tabState],
    );
    const progressSteps = useMemo(() => {
        if (activeTab === TAB_KEYS.BULK_EMAIL) {
            return [
                t(`${prefix}.progress.email.step1`),
                t(`${prefix}.progress.email.step2`),
                t(`${prefix}.progress.email.step3`),
            ];
        }
        if (STANDARD_NOTIFY_EMAIL_TAB_KEYS.has(activeTab)) {
            const progressKey = activeTab === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL
                ? 'notifyNotTriggeredProgress'
                : 'notifyProgress';
            return [
                t(`${prefix}.standard.${progressKey}.step1`),
                t(`${prefix}.standard.${progressKey}.step2`),
                t(`${prefix}.standard.${progressKey}.step3`),
            ];
        }
        if (activeTab === TAB_KEYS.BULK_SMS) {
            return [
                t(`${prefix}.progress.sms.step1`),
                t(`${prefix}.progress.sms.step2`),
                t(`${prefix}.progress.sms.step3`),
            ];
        }
        if (STANDARD_BACKUP_TAB_KEYS.has(activeTab)) {
            return [
                t(`${prefix}.standard.progress.step1`),
                t(`${prefix}.standard.progress.step2`),
                t(`${prefix}.standard.progress.step3`),
            ];
        }
        return [
            t(`${prefix}.progress.forecast.step1`),
            t(`${prefix}.progress.forecast.step2`),
            t(`${prefix}.progress.forecast.step3`),
            t(`${prefix}.progress.forecast.step4`),
            t(`${prefix}.progress.forecast.step5`),
            t(`${prefix}.progress.forecast.step6`),
        ];
    }, [activeTab, t]);
    const progressStepIntervalMs = (
        activeTab === TAB_KEYS.BULK_EMAIL
        || activeTab === TAB_KEYS.BULK_SMS
        || STANDARD_NOTIFY_EMAIL_TAB_KEYS.has(activeTab)
    ) ? 5000 : 8000;
    const { currentStep, elapsedSeconds } = useLongRunningProgress(
        current.loading,
        progressSteps,
        progressStepIntervalMs,
    );
    const partnerCompanyOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);
    const filteredPartnerOptions = useMemo(() => {
        const query = current.partnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [current.partnerSearch, partnerCompanyOptions]);

    useEffect(() => {
        const loadPartners = async () => {
            setPartnersLoading(true);
            setPartnersError(null);
            try {
                const response = await getAllPartners();
                setPartners(Array.isArray(response?.users) ? response.users : (Array.isArray(response) ? response : []));
            } catch (error) {
                setPartners([]);
                setPartnersError(formatRequestError(error, t(`${prefix}.bulkEmail.partnersError`), {
                    timeout: t(`${prefix}.errors.timeout`),
                    network: t(`${prefix}.errors.network`),
                }));
            } finally {
                setPartnersLoading(false);
            }
        };
        loadPartners();
    }, [language]);

    useEffect(() => {
        if (!isAnyTabLoading) return undefined;

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isAnyTabLoading]);

    const updateCurrentTab = (patch) => {
        setTabState((prev) => ({
            ...prev,
            [activeTab]: {
                ...(prev[activeTab] ?? emptyTabState()),
                ...patch,
            },
        }));
    };

    const handleRunBackup = async (event) => {
        event.preventDefault();
        if (activeTab === TAB_KEYS.BULK_EMAIL) {
            setShowEmailConfirm(true);
            return;
        }
        if (activeTab === TAB_KEYS.STANDARD_BULK_EMAIL) {
            if (!canRunStandardBackupEmail) return;
            setShowStandardEmailConfirm(true);
            return;
        }
        if (activeTab === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL) {
            if (!canRunStandardBackupEmail) return;
            setShowStandardNotTriggeredEmailConfirm(true);
            return;
        }
        if (activeTab === TAB_KEYS.BULK_SMS) {
            setShowSmsConfirm(true);
            return;
        }
        await executeBackupAction();
    };

    const executeBackupAction = async (forcedTabKey) => {
        const tabKey = forcedTabKey || activeTab;
        const tabSnapshot = tabState[tabKey] ?? emptyTabState();

        if (STANDARD_NOTIFY_EMAIL_TAB_KEYS.has(tabKey) && !canRunStandardBackupEmail) {
            setTabState((prev) => ({
                ...prev,
                [tabKey]: {
                    ...(prev[tabKey] ?? emptyTabState()),
                    loading: false,
                    error: t(`${prefix}.${getStandardNotifyEmailI18nKey(tabKey)}.errors.forbidden`),
                    result: null,
                },
            }));
            if (tabKey === TAB_KEYS.STANDARD_BULK_EMAIL) setShowStandardEmailConfirm(false);
            if (tabKey === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL) {
                setShowStandardNotTriggeredEmailConfirm(false);
            }
            return;
        }

        setTabState((prev) => ({
            ...prev,
            [tabKey]: {
                ...(prev[tabKey] ?? emptyTabState()),
                loading: true,
                error: null,
                result: null,
            },
        }));
        // Fermer les modals dès le lancement pour éviter un écran figé pendant l'appel long.
        if (tabKey === TAB_KEYS.BULK_EMAIL) setShowEmailConfirm(false);
        if (tabKey === TAB_KEYS.STANDARD_BULK_EMAIL) setShowStandardEmailConfirm(false);
        if (tabKey === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL) {
            setShowStandardNotTriggeredEmailConfirm(false);
        }
        if (tabKey === TAB_KEYS.BULK_SMS) setShowSmsConfirm(false);

        try {
            const params = {
                run_date: tabSnapshot.runDate.trim() || undefined,
            };
            if (POLICY_BACKUP_TAB_KEYS.has(tabKey)) {
                params.policy_code = tabSnapshot.policyCode.trim() || undefined;
            }

            let response;
            if (tabKey === TAB_KEYS.BULK_EMAIL) {
                response = await sendBackupForecastTriggeredBulkEmail({
                    trip_day: params.run_date,
                    partner_names: tabSnapshot.partnerNames,
                });
            } else if (tabKey === TAB_KEYS.STANDARD_BULK_EMAIL) {
                response = await sendStandardForecastTriggeredBackupEmails({
                    trip_day: params.run_date,
                    partner_names: tabSnapshot.partnerNames,
                });
            } else if (tabKey === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL) {
                response = await sendStandardForecastNotTriggeredBackupEmails({
                    trip_day: params.run_date,
                    partner_names: tabSnapshot.partnerNames,
                });
            } else if (tabKey === TAB_KEYS.BULK_SMS) {
                response = await sendBackupForecastTriggeredBulkSms({
                    trip_day: params.run_date,
                    partner_names: tabSnapshot.partnerNames,
                });
            } else if (STANDARD_BACKUP_TAB_KEYS.has(tabKey)) {
                response = await backupStandardCheckForecasts({
                    data_source: tabSnapshot.dataSource || DEFAULT_STANDARD_DATA_SOURCE,
                    run_date: params.run_date,
                    policy_code: params.policy_code,
                });
            } else {
                response = await backupTripsScheduledForecast(params);
            }

            setTabState((prev) => ({
                ...prev,
                [tabKey]: {
                    ...(prev[tabKey] ?? emptyTabState()),
                    result: response,
                    loading: false,
                },
            }));
            if (tabKey === TAB_KEYS.BULK_EMAIL) setShowEmailConfirm(false);
            if (tabKey === TAB_KEYS.STANDARD_BULK_EMAIL) setShowStandardEmailConfirm(false);
            if (tabKey === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL) {
                setShowStandardNotTriggeredEmailConfirm(false);
            }
            if (tabKey === TAB_KEYS.BULK_SMS) setShowSmsConfirm(false);
        } catch (requestError) {
            console.error('Forecast backup check failed:', requestError);
            const status = requestError?.response?.status;
            const detail = requestError?.response?.data?.detail;
            const apiMessage = typeof detail === 'string'
                ? detail
                : (detail && typeof detail === 'object' && detail.message ? String(detail.message) : null);
            const isStandardNotifyEmail = STANDARD_NOTIFY_EMAIL_TAB_KEYS.has(tabKey);
            const isStandardBackup = STANDARD_BACKUP_TAB_KEYS.has(tabKey);
            const standardNotifyI18nKey = getStandardNotifyEmailI18nKey(tabKey);
            let errorMessage;
            if (isStandardNotifyEmail && status === 400) {
                errorMessage = apiMessage || t(`${prefix}.${standardNotifyI18nKey}.errors.badRequest`);
            } else if (isStandardNotifyEmail && status === 403) {
                errorMessage = apiMessage || t(`${prefix}.${standardNotifyI18nKey}.errors.forbidden`);
            } else if (isStandardNotifyEmail && status === 404) {
                errorMessage = apiMessage || t(`${prefix}.${standardNotifyI18nKey}.errors.notFound`);
            } else if (isStandardBackup && status === 400) {
                errorMessage = apiMessage || t(`${prefix}.standard.errors.badRequest`);
            } else if (isStandardBackup && status === 403) {
                errorMessage = apiMessage || t(`${prefix}.standard.errors.forbidden`);
            } else if (isStandardBackup && status === 404) {
                errorMessage = apiMessage || t(`${prefix}.standard.errors.notFound`);
            } else {
                errorMessage = formatRequestError(requestError, t(`${prefix}.error`), {
                    timeout: t(`${prefix}.errors.timeout`),
                    network: t(`${prefix}.errors.network`),
                });
            }
            setTabState((prev) => ({
                ...prev,
                [tabKey]: {
                    ...(prev[tabKey] ?? emptyTabState()),
                    error: errorMessage,
                    loading: false,
                },
            }));
            if (tabKey === TAB_KEYS.BULK_EMAIL) setShowEmailConfirm(false);
            if (tabKey === TAB_KEYS.STANDARD_BULK_EMAIL) setShowStandardEmailConfirm(false);
            if (tabKey === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL) {
                setShowStandardNotTriggeredEmailConfirm(false);
            }
            if (tabKey === TAB_KEYS.BULK_SMS) setShowSmsConfirm(false);
        }
    };

    const handleReset = () => {
        setShowEmailConfirm(false);
        setShowStandardEmailConfirm(false);
        setShowStandardNotTriggeredEmailConfirm(false);
        setShowSmsConfirm(false);
        updateCurrentTab(emptyTabState());
    };

    const isStandardNotTriggeredEmailTab = activeTab === TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL;
    const resultStatus = current.result?.status
        ?? (current.result?.success === true ? 'success' : null)
        ?? (current.result?.success === false ? 'error' : null);
    const resultVariant = resultStatus === 'success' ? 'success' : (resultStatus === 'error' ? 'danger' : 'info');
    const checkClaimRows = useMemo(
        () => (isStandardNotTriggeredEmailTab
            ? []
            : buildCheckClaimRows(current.result?.check_claims)),
        [current.result?.check_claims, isStandardNotTriggeredEmailTab],
    );
    const standardCheckInfos = useMemo(() => {
        if (isStandardNotTriggeredEmailTab) return [];
        return getStandardCheckInfos(current.result);
    }, [current.result, isStandardNotTriggeredEmailTab]);
    const filteredStandardCheckInfos = useMemo(() => {
        if (standardInfosFilter === 'all') return standardCheckInfos;
        return standardCheckInfos.filter((info) => {
            const nested = getNestedCheckClaim(info);
            const triggered = Boolean(nested.is_triggered_forecast ?? info?.is_triggered_forecast);
            return standardInfosFilter === 'triggered' ? triggered : !triggered;
        });
    }, [standardCheckInfos, standardInfosFilter]);
    const checkClaimId = isCheckInfosPayload(current.result?.check_claims)
        ? null
        : current.result?.check_claims?.check_claim_id;
    const emailResultData = current.result?.data || {};
    const triggerEmails = isStandardNotTriggeredEmailTab
        ? (current.result?.not_triggered_emails && typeof current.result.not_triggered_emails === 'object'
            ? current.result.not_triggered_emails
            : {})
        : getStandardNotifyEmailPayload(current.result);
    const triggerEmailSummary = triggerEmails.summary && typeof triggerEmails.summary === 'object'
        ? triggerEmails.summary
        : {};
    const triggerEmailsSent = Array.isArray(triggerEmails.sent) ? triggerEmails.sent : [];
    const triggerEmailsSkipped = Array.isArray(triggerEmails.skipped) ? triggerEmails.skipped : [];
    const triggerEmailsFailed = Array.isArray(triggerEmails.failed) ? triggerEmails.failed : [];
    const emailsSent = isStandardNotTriggeredEmailTab
        ? triggerEmailSummary.sent_count
        : (current.result?.total_filtered_emails_sent
            ?? emailResultData.emails_sent
            ?? triggerEmailSummary.sent_count);
    const emailsSkipped = triggerEmailSummary.skipped_count;
    const emailsFailed = isStandardNotTriggeredEmailTab
        ? triggerEmailSummary.failed_count
        : (emailResultData.emails_failed ?? triggerEmailSummary.failed_count);
    const smsSent = isStandardNotTriggeredEmailTab
        ? null
        : (current.result?.total_filtered_sms_sent ?? emailResultData.sms_sent);
    const smsFailed = isStandardNotTriggeredEmailTab ? null : emailResultData.sms_failed;
    const checkClaimsMetadata = current.result?.check_claims?.metadata || {};
    const notTriggeredCheckInfosCount = isStandardNotTriggeredEmailTab
        ? (Array.isArray(current.result?.check_claims?.check_infos)
            ? current.result.check_claims.check_infos.length
            : (checkClaimsMetadata.count ?? null))
        : null;
    const policiesProcessed = isStandardNotTriggeredEmailTab
        ? notTriggeredCheckInfosCount
        : (emailResultData.total_policies
            ?? current.result?.summary?.total_policies_processed
            ?? checkClaimsMetadata.count);
    const policiesTriggered = isStandardNotTriggeredEmailTab
        ? null
        : current.result?.summary?.policies_triggered;
    const policiesWithErrors = isStandardNotTriggeredEmailTab
        ? null
        : current.result?.summary?.policies_with_errors;
    const resultForecastHour = current.result?.summary?.forecast_hour
        ?? checkClaimsMetadata.forecast_hour;
    const resultDataSource = isStandardNotTriggeredEmailTab
        ? null
        : (current.result?.data_source
            || current.result?.summary?.data_source
            || standardCheckInfos[0]?.data_source
            || (STANDARD_BACKUP_TAB_KEYS.has(activeTab) ? current.dataSource : null));
    const resultProcessingTime = formatProcessingTime(
        current.result?.summary?.processing_time_ms ?? checkClaimsMetadata.processing_time_ms,
    );
    const resultTimezone = checkClaimsMetadata.timezone;
    const emailDetails = isStandardNotTriggeredEmailTab
        ? []
        : (Array.isArray(emailResultData.details) ? emailResultData.details : []);
    const emailMeta = current.result?.meta || {};
    const emailTripDay = emailMeta.trip_day
        ?? checkClaimsMetadata.trip_day
        ?? current.result?.trip_day;
    const emailTimestamp = emailMeta.timestamp || current.result?.timestamp;
    const notifyI18nKey = activeTab === TAB_KEYS.BULK_SMS
        ? 'bulkSms'
        : (STANDARD_NOTIFY_EMAIL_TAB_KEYS.has(activeTab)
            ? getStandardNotifyEmailI18nKey(activeTab)
            : 'bulkEmail');

    const getFieldLabel = (fieldKey) => {
        const translated = t(`${prefix}.fields.${fieldKey}`);
        if (translated !== `${prefix}.fields.${fieldKey}`) {
            return translated;
        }
        return fieldKey.replace(/_/g, ' ');
    };

    const renderLoadingBanner = () => {
        const longWaitNote = getLongWaitMessage(elapsedSeconds, {
            stillNormal: t(`${prefix}.progress.stillNormal`),
            long: t(`${prefix}.progress.longWait`),
            twoMinutes: t(`${prefix}.progress.twoMinutesWait`),
            veryLong: t(`${prefix}.progress.veryLongWait`),
            overFourMinutes: t(`${prefix}.progress.overFourMinutesWait`),
        });

        return (
            <LongRunningOperationBanner
                show={current.loading}
                sticky
                title={t(`${prefix}.progress.title`)}
                expectedDuration={t(`${prefix}.progress.expectedDuration`)}
                message={currentStep}
                elapsedSeconds={elapsedSeconds}
                elapsedLabel={t(`${prefix}.progress.elapsed`)}
                longWaitNote={longWaitNote}
                hint={t(`${prefix}.progress.hint`)}
            />
        );
    };

    const renderResult = () => {
        if (current.error) {
            return (
                <Alert variant="danger" dismissible onClose={() => updateCurrentTab({ error: null })}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>{current.error}</span>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => executeBackupAction(activeTab)}
                            disabled={current.loading}
                        >
                            <FiRefreshCw size={14} className="me-1" aria-hidden />
                            {t(`${prefix}.retry`)}
                        </Button>
                    </div>
                </Alert>
            );
        }

        if (!current.result) return null;

        return (
            <>
                <Alert variant={resultVariant}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <strong>{t(`${prefix}.resultTitle`)}</strong>
                        {resultStatus && (
                            <Badge bg={resultStatus === 'success' ? 'success' : 'secondary'}>
                                {resultStatus}
                            </Badge>
                        )}
                    </div>
                    {current.result.message && <p className="mb-3">{current.result.message}</p>}
                    <Row className="g-3 small">
                        {current.result.status_code != null && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.statusCode`)}</span>
                                <div><strong>{current.result.status_code}</strong></div>
                            </Col>
                        )}
                        {emailTimestamp && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.timestamp`)}</span>
                                <div><strong>{emailTimestamp}</strong></div>
                            </Col>
                        )}
                        {emailTripDay && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.${notifyI18nKey}.tripDay`)}</span>
                                <div><strong>{emailTripDay}</strong></div>
                            </Col>
                        )}
                        {current.result.reference_date && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.referenceDate`)}</span>
                                <div><strong>{current.result.reference_date}</strong></div>
                            </Col>
                        )}
                        {current.result.policy_code && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.policyCode`)}</span>
                                <div><strong>{current.result.policy_code}</strong></div>
                            </Col>
                        )}
                        {resultDataSource && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.standard.dataSource`)}</span>
                                <div><strong>{resultDataSource}</strong></div>
                            </Col>
                        )}
                        {resultForecastHour != null && resultForecastHour !== '' && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.forecastHour`)}</span>
                                <div><strong>{resultForecastHour}</strong></div>
                            </Col>
                        )}
                        {current.result.count != null && !isStandardNotTriggeredEmailTab && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.count`)}</span>
                                <div><strong>{current.result.count}</strong></div>
                            </Col>
                        )}
                        {policiesProcessed != null && (
                            <Col md={4}>
                                <span className="text-muted">
                                    {isStandardNotTriggeredEmailTab
                                        ? t(`${prefix}.standardBulkNotTriggeredEmail.checkInfosCount`)
                                        : t(`${prefix}.policiesProcessed`)}
                                </span>
                                <div><strong>{policiesProcessed}</strong></div>
                            </Col>
                        )}
                        {policiesTriggered != null && !isStandardNotTriggeredEmailTab && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.policiesTriggered`)}</span>
                                <div><strong>{policiesTriggered}</strong></div>
                            </Col>
                        )}
                        {policiesWithErrors != null && !isStandardNotTriggeredEmailTab && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.policiesWithErrors`)}</span>
                                <div><strong>{policiesWithErrors}</strong></div>
                            </Col>
                        )}
                        {resultProcessingTime && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.processingTime`)}</span>
                                <div><strong>{resultProcessingTime}</strong></div>
                            </Col>
                        )}
                        {resultTimezone && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.timezone`)}</span>
                                <div><strong>{resultTimezone}</strong></div>
                            </Col>
                        )}
                        {emailsSent != null && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.emailsSent`)}</span>
                                <div><strong>{emailsSent}</strong></div>
                            </Col>
                        )}
                        {emailsSkipped != null && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.emailsSkipped`)}</span>
                                <div><strong>{emailsSkipped}</strong></div>
                            </Col>
                        )}
                        {emailsFailed != null && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.emailsFailed`)}</span>
                                <div><strong>{emailsFailed}</strong></div>
                            </Col>
                        )}
                        {smsSent != null && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.smsSent`)}</span>
                                <div><strong>{smsSent}</strong></div>
                            </Col>
                        )}
                        {smsFailed != null && (
                            <Col md={4}>
                                <span className="text-muted">{t(`${prefix}.smsFailed`)}</span>
                                <div><strong>{smsFailed}</strong></div>
                            </Col>
                        )}
                    </Row>
                </Alert>

                {emailDetails.length > 0 && (
                    <Card className="mt-3">
                        <Card.Header>
                            <h6 className="mb-0">{t(`${prefix}.${notifyI18nKey}.detailsTitle`)}</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive striped hover className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>{t(`${prefix}.${notifyI18nKey}.colPolicyCode`)}</th>
                                        <th>{t(`${prefix}.${notifyI18nKey}.colStatus`)}</th>
                                        <th>{t(`${prefix}.${notifyI18nKey}.colReason`)}</th>
                                        <th>{t(`${prefix}.${notifyI18nKey}.colPolicyId`)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {emailDetails.map((row) => {
                                        const status = String(row.status || '').toLowerCase();
                                        const statusBg = status === 'sent' || status === 'success'
                                            ? 'success'
                                            : status === 'failed' || status === 'error'
                                                ? 'danger'
                                                : 'secondary';
                                        return (
                                            <tr key={row.policy_id || `${row.policy_code}-${row.status}`}>
                                                <td><strong>{row.policy_code || '—'}</strong></td>
                                                <td>
                                                    <Badge bg={statusBg}>
                                                        {t(`${prefix}.${notifyI18nKey}.rowStatus.${status}`) !== `${prefix}.${notifyI18nKey}.rowStatus.${status}`
                                                            ? t(`${prefix}.${notifyI18nKey}.rowStatus.${status}`)
                                                            : (row.status || '—')}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {t(`${prefix}.${notifyI18nKey}.reasons.${row.reason}`) !== `${prefix}.${notifyI18nKey}.reasons.${row.reason}`
                                                        ? t(`${prefix}.${notifyI18nKey}.reasons.${row.reason}`)
                                                        : (row.reason || '—')}
                                                </td>
                                                <td><code className="small">{row.policy_id || '—'}</code></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                )}

                {(triggerEmailsSent.length > 0
                    || triggerEmailsSkipped.length > 0
                    || triggerEmailsFailed.length > 0) && (
                    <>
                        {triggerEmailsSent.length > 0 && (
                            <Card className="mt-3">
                                <Card.Header>
                                    <h6 className="mb-0">{t(`${prefix}.${notifyI18nKey}.sentTitle`)}</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive striped hover className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colPolicyCode`)}</th>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colClaim`)}</th>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colRecipients`)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {triggerEmailsSent.map((row, index) => (
                                                <tr key={`sent-${row.policy_code || index}-${row.claim_id || index}`}>
                                                    <td><strong>{row.policy_code || '—'}</strong></td>
                                                    <td><code className="small">{row.claim_id || '—'}</code></td>
                                                    <td>{formatRecipients(row.recipients)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}
                        {triggerEmailsSkipped.length > 0 && (
                            <Card className="mt-3">
                                <Card.Header>
                                    <h6 className="mb-0">{t(`${prefix}.${notifyI18nKey}.skippedTitle`)}</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive striped hover className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colPolicyCode`)}</th>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colClaim`)}</th>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colReason`)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {triggerEmailsSkipped.map((row, index) => (
                                                <tr key={`skipped-${row.policy_code || index}-${row.claim_id || index}`}>
                                                    <td><strong>{row.policy_code || '—'}</strong></td>
                                                    <td><code className="small">{row.claim_id || '—'}</code></td>
                                                    <td>{row.reason || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}
                        {triggerEmailsFailed.length > 0 && (
                            <Card className="mt-3">
                                <Card.Header>
                                    <h6 className="mb-0">{t(`${prefix}.${notifyI18nKey}.failedTitle`)}</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive striped hover className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colPolicyCode`)}</th>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colClaim`)}</th>
                                                <th>{t(`${prefix}.${notifyI18nKey}.colError`)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {triggerEmailsFailed.map((row, index) => (
                                                <tr key={`failed-${row.policy_code || index}-${row.claim_id || index}`}>
                                                    <td><strong>{row.policy_code || '—'}</strong></td>
                                                    <td><code className="small">{row.claim_id || '—'}</code></td>
                                                    <td className="text-break">{row.error || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}
                    </>
                )}

                {standardCheckInfos.length > 0 && (
                    <Card className="mt-3">
                        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <h6 className="mb-0">{t(`${prefix}.standard.infosTitle`)}</h6>
                            <div className="d-flex flex-wrap gap-1">
                                {[
                                    { id: 'all', label: t(`${prefix}.standard.filterAll`) },
                                    { id: 'triggered', label: t(`${prefix}.standard.filterTriggered`) },
                                    { id: 'notTriggered', label: t(`${prefix}.standard.filterNotTriggered`) },
                                ].map((filter) => (
                                    <Button
                                        key={filter.id}
                                        size="sm"
                                        variant={standardInfosFilter === filter.id ? 'primary' : 'outline-secondary'}
                                        onClick={() => setStandardInfosFilter(filter.id)}
                                    >
                                        {filter.label}
                                    </Button>
                                ))}
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {filteredStandardCheckInfos.length === 0 ? (
                                <p className="mb-0 p-3 text-muted">{t(`${prefix}.standard.noInfos`)}</p>
                            ) : (
                                <Table responsive striped hover className="mb-0 align-middle fb-infos-table">
                                    <thead>
                                        <tr>
                                            <th>{t(`${prefix}.standard.colPolicy`)}</th>
                                            <th>{t(`${prefix}.standard.colClient`)}</th>
                                            <th>{t(`${prefix}.standard.colTripDay`)}</th>
                                            <th>{t(`${prefix}.standard.colSource`)}</th>
                                            <th>{t(`${prefix}.standard.colTriggered`)}</th>
                                            <th>{t(`${prefix}.standard.colPrecip`)}</th>
                                            <th>{t(`${prefix}.standard.colTemp`)}</th>
                                            <th>{t(`${prefix}.standard.colHours`)}</th>
                                            <th>{t(`${prefix}.standard.colOutcome`)}</th>
                                            <th>{t(`${prefix}.standard.colCheckClaim`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStandardCheckInfos.map((info, index) => {
                                            const nested = getNestedCheckClaim(info);
                                            const triggered = Boolean(
                                                nested.is_triggered_forecast ?? info?.is_triggered_forecast,
                                            );
                                            const precipTriggered = nested.precipitation_is_triggered_forecast
                                                ?? info?.precipitation_is_triggered_forecast;
                                            const tempTriggered = nested.temperature_is_triggered_forecast
                                                ?? info?.temperature_is_triggered_forecast;
                                            const hours = nested.count_hours_triggered
                                                ?? info?.count_hours_triggered;
                                            const nestedCheckClaimId = nested.check_claim_id;
                                            const policyId = info?.policy_id || info?.policy_info?.policy_id;
                                            const claimInfo = isNonEmptyObject(info?.claim_info) ? info.claim_info : null;
                                            const notTriggered = isNonEmptyObject(info?.forecast_not_triggered)
                                                ? info.forecast_not_triggered
                                                : null;
                                            const rowKey = nestedCheckClaimId
                                                || policyId
                                                || info?.forecast_id
                                                || `${info?.policy_code || 'policy'}-${index}`;

                                            return (
                                                <tr key={rowKey}>
                                                    <td>
                                                        {policyId ? (
                                                            <Button
                                                                variant="link"
                                                                className="p-0 fw-semibold"
                                                                onClick={() => navigate(`/admin/trips/policies/${policyId}`)}
                                                            >
                                                                {info?.policy_code || policyId}
                                                            </Button>
                                                        ) : (
                                                            <strong>{info?.policy_code || '—'}</strong>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>{formatClientName(info?.client_info)}</div>
                                                        {info?.activity_info?.activity_city && (
                                                            <div className="text-muted small">
                                                                {info.activity_info.activity_city}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{info?.trip_day || '—'}</td>
                                                    <td>{info?.data_source || '—'}</td>
                                                    <td>
                                                        <Badge bg={triggered ? 'success' : 'secondary'}>
                                                            {triggered
                                                                ? t(`${prefix}.standard.triggeredYes`)
                                                                : t(`${prefix}.standard.triggeredNo`)}
                                                        </Badge>
                                                    </td>
                                                    <td>{formatDisplayValue(precipTriggered)}</td>
                                                    <td>{formatDisplayValue(tempTriggered)}</td>
                                                    <td>{hours == null ? '—' : hours}</td>
                                                    <td>
                                                        {claimInfo ? (
                                                            <div>
                                                                <Badge bg="success" className="me-1">
                                                                    {claimInfo.claim_status || t(`${prefix}.standard.outcomeClaim`)}
                                                                </Badge>
                                                                {claimInfo.claim_code && (
                                                                    <code className="small">{claimInfo.claim_code}</code>
                                                                )}
                                                            </div>
                                                        ) : notTriggered ? (
                                                            <div>
                                                                <Badge bg="secondary">
                                                                    {t(`${prefix}.standard.outcomeNotTriggered`)}
                                                                </Badge>
                                                                {notTriggered.code && (
                                                                    <div className="small text-muted">{notTriggered.code}</div>
                                                                )}
                                                            </div>
                                                        ) : '—'}
                                                    </td>
                                                    <td>
                                                        {nestedCheckClaimId ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => navigate(`/admin/trips/check-claims/${nestedCheckClaimId}`)}
                                                            >
                                                                <FiExternalLink className="me-1" />
                                                                {t(`${prefix}.viewCheckClaim`)}
                                                            </Button>
                                                        ) : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {checkClaimRows.length > 0 && (
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">{t(`${prefix}.checkClaimsTitle`)}</h6>
                            {checkClaimId && (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => navigate(`/admin/trips/check-claims/${checkClaimId}`)}
                                >
                                    <FiExternalLink className="me-1" />
                                    {t(`${prefix}.viewCheckClaim`)}
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive striped bordered hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40%' }}>{t(`${prefix}.field`)}</th>
                                        <th>{t(`${prefix}.value`)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checkClaimRows.map(({ key, value }) => (
                                        <tr key={key}>
                                            <td className="text-muted">{getFieldLabel(key)}</td>
                                            <td>{formatDisplayValue(value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                )}
            </>
        );
    };

    const renderParametersForm = ({ idPrefix = 'backup-parameters', variant = 'ecmwf' } = {}) => {
        const isStandard = variant === 'standard';
        const selectedSource = current.dataSource || DEFAULT_STANDARD_DATA_SOURCE;
        const endpointPath = isStandard
            ? `/api/standard/trips/backup/check-forecasts/${selectedSource}`
            : '/api/backup/ecmwf-v2/trips/scheduled-forecast';

        return (
        <div className={`fb-panel${current.loading ? ' fb-panel--busy' : ''}`}>
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <FiTarget size={22} aria-hidden />
                    {t(`${prefix}.pageTabs.parameters`)}
                </h3>
                <p className="fb-panel__desc">
                    {isStandard ? t(`${prefix}.standard.description`) : t(`${prefix}.description`)}
                </p>
                <p className="fb-duration-notice" role="note">{t(`${prefix}.progress.expectedDuration`)}</p>
                <div className="fb-endpoint" role="note">
                    <span className="fb-endpoint__method">GET</span>
                    <code>{endpointPath}</code>
                </div>
            </div>
            <div className="fb-panel__body">
                <Form id={`${idPrefix}-form`} onSubmit={handleRunBackup}>
                    <div className="fb-fields">
                        {isStandard && (
                            <div className="fb-field">
                                <label className="fb-field__label" htmlFor={`${idPrefix}-data-source`}>
                                    {t(`${prefix}.standard.dataSource`)}
                                </label>
                                <Form.Select
                                    id={`${idPrefix}-data-source`}
                                    value={selectedSource}
                                    onChange={(e) => updateCurrentTab({ dataSource: e.target.value })}
                                    disabled={current.loading}
                                >
                                    {STANDARD_BACKUP_DATA_SOURCES.map((source) => (
                                        <option key={source} value={source}>{source}</option>
                                    ))}
                                </Form.Select>
                                <p className="fb-field__help">{t(`${prefix}.standard.dataSourceHint`)}</p>
                            </div>
                        )}
                        <div className="fb-field">
                            <label className="fb-field__label" htmlFor={`${idPrefix}-run-date`}>
                                {t(`${prefix}.runDate`)}
                            </label>
                            <Form.Control
                                id={`${idPrefix}-run-date`}
                                type="date"
                                value={current.runDate}
                                onChange={(e) => updateCurrentTab({ runDate: e.target.value })}
                                disabled={current.loading}
                            />
                            <p className="fb-field__help">{t(`${prefix}.runDateHint`)}</p>
                        </div>
                        <div className="fb-field">
                            <label className="fb-field__label" htmlFor={`${idPrefix}-policy-code`}>
                                {t(`${prefix}.policyCode`)}
                            </label>
                            <Form.Control
                                id={`${idPrefix}-policy-code`}
                                type="text"
                                value={current.policyCode}
                                onChange={(e) => updateCurrentTab({ policyCode: e.target.value })}
                                placeholder={t(`${prefix}.policyCodePlaceholder`)}
                                disabled={current.loading}
                            />
                            <p className="fb-field__help">{t(`${prefix}.policyCodeHint`)}</p>
                        </div>
                    </div>
                </Form>
            </div>
            <div className="fb-panel__footer">
                <Button type="submit" form={`${idPrefix}-form`} className="btn fb-btn-primary" disabled={current.loading}>
                    {current.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${prefix}.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${prefix}.runButton`)}
                        </>
                    )}
                </Button>
                <Button type="button" className="btn fb-btn-secondary" onClick={handleReset} disabled={current.loading}>
                    <FiRefreshCw size={16} aria-hidden />
                    {t('common.reset')}
                </Button>
            </div>
        </div>
        );
    };

    const renderBulkForm = ({ idPrefix = 'backup-bulk', variant = 'ecmwf' } = {}) => {
        const isStandard = variant === 'standard';
        const selectedSource = current.dataSource || DEFAULT_STANDARD_DATA_SOURCE;
        const endpointPath = isStandard
            ? `/api/standard/trips/backup/check-forecasts/${selectedSource}`
            : '/api/backup/ecmwf-v2/trips/scheduled-forecast';

        return (
        <div className={`fb-panel${current.loading ? ' fb-panel--busy' : ''}`}>
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <FiLayers size={22} aria-hidden />
                    {t(`${prefix}.pageTabs.bulk`)}
                </h3>
                <p className="fb-panel__desc">
                    {isStandard ? t(`${prefix}.standard.bulkDescription`) : t(`${prefix}.bulk.description`)}
                </p>
                <p className="fb-duration-notice" role="note">{t(`${prefix}.progress.expectedDuration`)}</p>
                <div className="fb-endpoint" role="note">
                    <span className="fb-endpoint__method">GET</span>
                    <code>{endpointPath}</code>
                </div>
            </div>
            <div className="fb-panel__body">
                <div className="fb-alert-warning" role="status">
                    <FiLayers size={18} aria-hidden />
                    <span>{isStandard ? t(`${prefix}.standard.bulkInfo`) : t(`${prefix}.bulk.info`)}</span>
                </div>
                <Form id={`${idPrefix}-form`} onSubmit={handleRunBackup}>
                    <div className="fb-fields">
                        {isStandard && (
                            <div className="fb-field">
                                <label className="fb-field__label" htmlFor={`${idPrefix}-data-source`}>
                                    {t(`${prefix}.standard.dataSource`)}
                                </label>
                                <Form.Select
                                    id={`${idPrefix}-data-source`}
                                    value={selectedSource}
                                    onChange={(e) => updateCurrentTab({ dataSource: e.target.value })}
                                    disabled={current.loading}
                                >
                                    {STANDARD_BACKUP_DATA_SOURCES.map((source) => (
                                        <option key={source} value={source}>{source}</option>
                                    ))}
                                </Form.Select>
                                <p className="fb-field__help">{t(`${prefix}.standard.dataSourceHint`)}</p>
                            </div>
                        )}
                        <div className="fb-field">
                            <label className="fb-field__label" htmlFor={`${idPrefix}-run-date`}>
                                {t(`${prefix}.runDate`)}
                            </label>
                            <Form.Control
                                id={`${idPrefix}-run-date`}
                                type="date"
                                value={current.runDate}
                                onChange={(e) => updateCurrentTab({ runDate: e.target.value })}
                                disabled={current.loading}
                            />
                            <p className="fb-field__help">{t(`${prefix}.runDateHint`)}</p>
                        </div>
                    </div>
                </Form>
            </div>
            <div className="fb-panel__footer">
                <Button type="submit" form={`${idPrefix}-form`} className="btn fb-btn-primary" disabled={current.loading}>
                    {current.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${prefix}.bulk.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${prefix}.bulk.runButton`)}
                        </>
                    )}
                </Button>
                <Button type="button" className="btn fb-btn-secondary" onClick={handleReset} disabled={current.loading}>
                    <FiRefreshCw size={16} aria-hidden />
                    {t('common.reset')}
                </Button>
            </div>
        </div>
        );
    };

    const emailTab = tabState[TAB_KEYS.BULK_EMAIL] ?? emptyTabState();
    const standardEmailTab = tabState[TAB_KEYS.STANDARD_BULK_EMAIL] ?? emptyTabState();
    const standardNotTriggeredEmailTab = tabState[TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL] ?? emptyTabState();
    const smsTab = tabState[TAB_KEYS.BULK_SMS] ?? emptyTabState();

    const renderPartnerFilter = (idPrefix, i18nKey) => (
        <div className="fb-field">
            <label className="fb-field__label" htmlFor={`${idPrefix}-partner-search`}>
                {t(`${prefix}.${i18nKey}.partners`)}
            </label>
            <Form.Control
                id={`${idPrefix}-partner-search`}
                type="text"
                value={current.partnerSearch}
                onChange={(event) => updateCurrentTab({ partnerSearch: event.target.value })}
                placeholder={t(`${prefix}.${i18nKey}.partnerSearch`)}
                disabled={current.loading || partnersLoading}
            />
            <div className="fb-partner-list">
                <Form.Check
                    type="checkbox"
                    id={`${idPrefix}-all-partners`}
                    label={t(`${prefix}.${i18nKey}.allPartners`)}
                    checked={current.partnerNames.length === 0}
                    disabled={current.loading || partnersLoading}
                    onChange={() => updateCurrentTab({ partnerNames: [] })}
                    className="fb-partner-row is-master"
                />
                {filteredPartnerOptions.length === 0 && !partnersLoading ? (
                    <div className="text-muted small py-1">{t(`${prefix}.${i18nKey}.noPartner`)}</div>
                ) : (
                    filteredPartnerOptions.map((companyName, index) => (
                        <Form.Check
                            key={companyName}
                            type="checkbox"
                            id={`${idPrefix}-partner-${index}`}
                            label={companyName}
                            checked={current.partnerNames.includes(companyName)}
                            disabled={current.loading || partnersLoading}
                            onChange={(event) => updateCurrentTab({
                                partnerNames: event.target.checked
                                    ? [...current.partnerNames, companyName]
                                    : current.partnerNames.filter((name) => name !== companyName),
                            })}
                            className="fb-partner-row"
                        />
                    ))
                )}
            </div>
            {partnersLoading && <div className="fb-field__help">{t('common.loading')}</div>}
            {partnersError && <div className="text-danger small mt-1">{partnersError}</div>}
            {current.partnerNames.length > 0 && (
                <div className="fb-selected">
                    {current.partnerNames.map((name) => (
                        <Badge bg="secondary" key={name}>{name}</Badge>
                    ))}
                </div>
            )}
            <p className="fb-field__help">
                {t(`${prefix}.${i18nKey}.partnersHint`)}
            </p>
        </div>
    );

    const renderNotifyConfirmModal = ({
        show,
        onHide,
        tabData,
        i18nKey,
        Icon,
        tabKey,
        modalClassName,
    }) => (
        <Modal
            show={show}
            onHide={() => !tabData.loading && onHide()}
            centered
            className={modalClassName}
        >
            <Modal.Header closeButton={!tabData.loading}>
                <Modal.Title>
                    <Icon className="me-2" />
                    {t(`${prefix}.${i18nKey}.confirmTitle`)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2">{t(`${prefix}.${i18nKey}.confirmMessage`)}</p>
                <p className="text-muted mb-3">{t(`${prefix}.${i18nKey}.confirmDescription`)}</p>
                <p className="mb-1">
                    <strong>{t(`${prefix}.${i18nKey}.tripDay`)} :</strong>{' '}
                    {tabData.runDate || t(`${prefix}.${i18nKey}.tripDayDefault`)}
                </p>
                <p className="mb-1"><strong>{t(`${prefix}.${i18nKey}.targetedPartners`)} :</strong></p>
                {tabData.partnerNames.length > 0 ? (
                    <ul className="mb-0">
                        {tabData.partnerNames.map((name) => (
                            <li key={`confirm-${i18nKey}-${name}`}>{name}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="mb-0 text-muted">{t(`${prefix}.${i18nKey}.allPartnersTarget`)}</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    type="button"
                    variant="outline-secondary"
                    className="btn fb-btn-secondary"
                    onClick={onHide}
                    disabled={tabData.loading}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    className="btn fb-btn-primary"
                    onClick={() => executeBackupAction(tabKey)}
                    disabled={tabData.loading}
                >
                    {tabData.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${prefix}.${i18nKey}.running`)}
                        </>
                    ) : (
                        <>
                            <Icon size={16} aria-hidden />
                            {t(`${prefix}.${i18nKey}.confirmSend`)}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    const renderBulkNotifyForm = ({
        i18nKey,
        endpointPath,
        Icon,
        idPrefix,
        confirmShow,
        confirmHide,
        confirmTabData,
        confirmTabKey,
        modalClassName,
        title,
        canSubmit = true,
    }) => (
        <div className={`fb-panel${current.loading ? ' fb-panel--busy' : ''}`}>
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <Icon size={22} aria-hidden />
                    {title}
                </h3>
                <p className="fb-panel__desc">{t(`${prefix}.${i18nKey}.description`)}</p>
                <div className="fb-endpoint" role="note">
                    <span className="fb-endpoint__method">GET</span>
                    <code>{endpointPath}</code>
                </div>
            </div>

            <div className="fb-panel__body">
                <div className="fb-alert-warning" role="alert">
                    <Icon size={18} aria-hidden />
                    <span>{t(`${prefix}.${i18nKey}.warning`)}</span>
                </div>
                {!canSubmit && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        {t(`${prefix}.${i18nKey}.errors.forbidden`)}
                    </Alert>
                )}

                <Form id={`${idPrefix}-form`} onSubmit={handleRunBackup}>
                    <div className="fb-fields">
                        <div className="fb-field">
                            <label className="fb-field__label" htmlFor={`${idPrefix}-trip-day`}>
                                {t(`${prefix}.${i18nKey}.tripDay`)}
                            </label>
                            <Form.Control
                                id={`${idPrefix}-trip-day`}
                                type="date"
                                value={current.runDate}
                                onChange={(event) => updateCurrentTab({ runDate: event.target.value })}
                                disabled={current.loading}
                            />
                            <p className="fb-field__help">
                                {t(`${prefix}.${i18nKey}.runDateHint`)}
                            </p>
                        </div>
                        {renderPartnerFilter(idPrefix, i18nKey)}
                    </div>
                </Form>
            </div>

            <div className="fb-panel__footer">
                <Button
                    type="submit"
                    form={`${idPrefix}-form`}
                    className="btn fb-btn-primary"
                    disabled={current.loading || !canSubmit}
                >
                    {current.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${prefix}.${i18nKey}.running`)}
                        </>
                    ) : (
                        <>
                            <Icon size={16} aria-hidden />
                            {t(`${prefix}.${i18nKey}.runButton`)}
                        </>
                    )}
                </Button>
                <Button
                    type="button"
                    className="btn fb-btn-secondary"
                    onClick={handleReset}
                    disabled={current.loading}
                >
                    <FiRefreshCw size={16} aria-hidden />
                    {t('common.reset')}
                </Button>
            </div>

            {renderNotifyConfirmModal({
                show: confirmShow,
                onHide: confirmHide,
                tabData: confirmTabData,
                i18nKey,
                Icon,
                tabKey: confirmTabKey,
                modalClassName,
            })}
        </div>
    );

    const renderBulkEmailForm = () => renderBulkNotifyForm({
        i18nKey: 'bulkEmail',
        endpointPath: '/api/backup_ecmwf/trips/forecast-triggered/bulk-send-email',
        Icon: FiMail,
        idPrefix: 'backup-email',
        confirmShow: showEmailConfirm,
        confirmHide: () => setShowEmailConfirm(false),
        confirmTabData: emailTab,
        confirmTabKey: TAB_KEYS.BULK_EMAIL,
        modalClassName: 'backup-email-confirm-modal',
        title: t(`${prefix}.pageTabs.bulkEmail`),
    });

    const renderBulkSmsForm = () => renderBulkNotifyForm({
        i18nKey: 'bulkSms',
        endpointPath: '/api/backup_ecmwf/trips/forecast-triggered/bulk-send-sms',
        Icon: FiMessageSquare,
        idPrefix: 'backup-sms',
        confirmShow: showSmsConfirm,
        confirmHide: () => setShowSmsConfirm(false),
        confirmTabData: smsTab,
        confirmTabKey: TAB_KEYS.BULK_SMS,
        modalClassName: 'backup-email-confirm-modal',
        title: t(`${prefix}.pageTabs.bulkSms`),
    });

    const renderStandardBulkEmailForm = () => renderBulkNotifyForm({
        i18nKey: 'standardBulkEmail',
        endpointPath: '/api/standard/trips/forecasts-triggered/backup-send-email',
        Icon: FiMail,
        idPrefix: 'backup-standard-email',
        confirmShow: showStandardEmailConfirm,
        confirmHide: () => setShowStandardEmailConfirm(false),
        confirmTabData: standardEmailTab,
        confirmTabKey: TAB_KEYS.STANDARD_BULK_EMAIL,
        modalClassName: 'backup-email-confirm-modal',
        title: t(`${prefix}.pageTabs.bulkEmail`),
        canSubmit: canRunStandardBackupEmail,
    });

    const renderStandardBulkNotTriggeredEmailForm = () => renderBulkNotifyForm({
        i18nKey: 'standardBulkNotTriggeredEmail',
        endpointPath: '/api/standard/trips/forecasts-not-triggered/backup-send-email',
        Icon: FiMail,
        idPrefix: 'backup-standard-not-triggered-email',
        confirmShow: showStandardNotTriggeredEmailConfirm,
        confirmHide: () => setShowStandardNotTriggeredEmailConfirm(false),
        confirmTabData: standardNotTriggeredEmailTab,
        confirmTabKey: TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL,
        modalClassName: 'backup-email-confirm-modal',
        title: t(`${prefix}.pageTabs.bulkEmailNotTriggered`),
        canSubmit: canRunStandardBackupEmail,
    });

    return (
        <div className="dashboard-page admin-trips-forecast-backup-check-page">
            <header className="fb-page-header">
                <h1 className="fb-page-title">
                    <FiCloudRain size={28} aria-hidden />
                    {t(`${prefix}.title`)}
                </h1>
                <p className="fb-page-subtitle">{t(`${prefix}.subtitle`)}</p>
            </header>

            {current.loading
                && activeTab !== TAB_KEYS.IMPORT_EXCEL
                && activeTab !== TAB_KEYS.STANDARD_IMPORT_EXCEL
                && !isStandardNotifySmsPlaceholder && (
                <div className="fb-global-progress">
                    {renderLoadingBanner()}
                </div>
            )}

            <Tab.Container
                activeKey={mainTab}
                onSelect={(key) => {
                    if (key && !isAnyTabLoading) setMainTab(key);
                }}
            >
                <Nav variant="tabs" className="fb-main-tabs" role="tablist">
                    <Nav.Item>
                        <Nav.Link eventKey={MAIN_TAB_KEYS.BACKUP_TOOLS} disabled={isAnyTabLoading}>
                            <FiCloudRain size={16} aria-hidden />
                            {t(`${prefix}.pageTabs.backupTools`)}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey={MAIN_TAB_KEYS.NOTIFICATION} disabled={isAnyTabLoading}>
                            <FiBell size={16} aria-hidden />
                            {t(`${prefix}.pageTabs.notification`)}
                        </Nav.Link>
                    </Nav.Item>
                    {canBulkDeleteCheckForecast && (
                        <Nav.Item>
                            <Nav.Link eventKey={MAIN_TAB_KEYS.CHECK_FORECAST_DELETE} disabled={isAnyTabLoading}>
                                <FiTrash2 size={16} aria-hidden />
                                {t(`${prefix}.pageTabs.checkForecastDelete`)}
                            </Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey={MAIN_TAB_KEYS.BACKUP_TOOLS}>
                        <Tab.Container
                            activeKey={forecastFamily}
                            onSelect={(key) => {
                                if (key && !isAnyTabLoading) setForecastFamily(key);
                            }}
                        >
                            <Nav className="fb-segmented" role="tablist" aria-label={t(`${prefix}.pageTabs.backupTools`)}>
                                <Nav.Item>
                                    <Nav.Link eventKey={FORECAST_FAMILY_KEYS.STANDARD} disabled={isAnyTabLoading}>
                                        {t(`${prefix}.pageTabs.standard`)}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey={FORECAST_FAMILY_KEYS.SPECIFIC} disabled={isAnyTabLoading}>
                                        {t(`${prefix}.pageTabs.specific`)}
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <Tab.Content>
                                <Tab.Pane eventKey={FORECAST_FAMILY_KEYS.STANDARD}>
                                    <Tab.Container
                                        activeKey={backupStandardSubTab}
                                        onSelect={(key) => {
                                            if (key && !isAnyTabLoading) setBackupStandardSubTab(key);
                                        }}
                                    >
                                        <Nav
                                            className="fb-segmented fb-segmented--sub"
                                            role="tablist"
                                            aria-label={t(`${prefix}.pageTabs.standard`)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.STANDARD_PARAMETERS} disabled={isAnyTabLoading}>
                                                    <FiTarget size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.parameters`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK} disabled={isAnyTabLoading}>
                                                    <FiLayers size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulk`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.STANDARD_IMPORT_EXCEL} disabled={isAnyTabLoading}>
                                                    <FiUploadCloud size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.importExcel`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                        <Tab.Content>
                                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_PARAMETERS}>
                                                {renderParametersForm({ idPrefix: 'backup-standard-parameters', variant: 'standard' })}
                                            </Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK}>
                                                {renderBulkForm({ idPrefix: 'backup-standard-bulk', variant: 'standard' })}
                                            </Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_IMPORT_EXCEL}>
                                                <div className="fb-panel">
                                                    <div className="fb-panel__body">
                                                        <StandardScheduledForecastExcelImportTab />
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </Tab.Pane>
                                <Tab.Pane eventKey={FORECAST_FAMILY_KEYS.SPECIFIC}>
                                    <Tab.Container
                                        activeKey={backupSubTab}
                                        onSelect={(key) => {
                                            if (key && !isAnyTabLoading) setBackupSubTab(key);
                                        }}
                                    >
                                        <Nav
                                            className="fb-segmented fb-segmented--sub"
                                            role="tablist"
                                            aria-label={t(`${prefix}.pageTabs.specific`)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.PARAMETERS} disabled={isAnyTabLoading}>
                                                    <FiTarget size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.parameters`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.BULK} disabled={isAnyTabLoading}>
                                                    <FiLayers size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulk`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.IMPORT_EXCEL} disabled={isAnyTabLoading}>
                                                    <FiUploadCloud size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.importExcel`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                        <Tab.Content>
                                            <Tab.Pane eventKey={TAB_KEYS.PARAMETERS}>{renderParametersForm()}</Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.BULK}>{renderBulkForm()}</Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.IMPORT_EXCEL}>
                                                <div className="fb-panel">
                                                    <div className="fb-panel__body">
                                                        <EcmwfV2ExcelImportTab />
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </Tab.Pane>

                    <Tab.Pane eventKey={MAIN_TAB_KEYS.NOTIFICATION}>
                        <Tab.Container
                            activeKey={notifyFamily}
                            onSelect={(key) => {
                                if (key && !isAnyTabLoading) setNotifyFamily(key);
                            }}
                        >
                            <Nav className="fb-segmented" role="tablist" aria-label={t(`${prefix}.pageTabs.notification`)}>
                                <Nav.Item>
                                    <Nav.Link eventKey={FORECAST_FAMILY_KEYS.STANDARD} disabled={isAnyTabLoading}>
                                        {t(`${prefix}.pageTabs.standard`)}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey={FORECAST_FAMILY_KEYS.SPECIFIC} disabled={isAnyTabLoading}>
                                        {t(`${prefix}.pageTabs.specific`)}
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <Tab.Content>
                                <Tab.Pane eventKey={FORECAST_FAMILY_KEYS.STANDARD}>
                                    <Tab.Container
                                        activeKey={notifyStandardSubTab}
                                        onSelect={(key) => {
                                            if (key && !isAnyTabLoading) setNotifyStandardSubTab(key);
                                        }}
                                    >
                                        <Nav
                                            className="fb-segmented fb-segmented--sub"
                                            role="tablist"
                                            aria-label={t(`${prefix}.pageTabs.standard`)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK_EMAIL} disabled={isAnyTabLoading}>
                                                    <FiMail size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulkEmail`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL} disabled={isAnyTabLoading}>
                                                    <FiMail size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulkEmailNotTriggered`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK_SMS} disabled={isAnyTabLoading}>
                                                    <FiMessageSquare size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulkSms`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                        <Tab.Content>
                                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK_EMAIL}>
                                                {renderStandardBulkEmailForm()}
                                            </Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL}>
                                                {renderStandardBulkNotTriggeredEmailForm()}
                                            </Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK_SMS}>
                                                <div className="fb-panel fb-placeholder" role="status">
                                                    <h3 className="fb-panel__title mb-2">
                                                        {t(`${prefix}.pageTabs.bulkSms`)}
                                                    </h3>
                                                    <p className="mb-0 text-muted">
                                                        {t(`${prefix}.standard.notifySoon`)}
                                                    </p>
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </Tab.Pane>
                                <Tab.Pane eventKey={FORECAST_FAMILY_KEYS.SPECIFIC}>
                                    <Tab.Container
                                        activeKey={notifySubTab}
                                        onSelect={(key) => {
                                            if (key && !isAnyTabLoading) setNotifySubTab(key);
                                        }}
                                    >
                                        <Nav
                                            className="fb-segmented fb-segmented--sub"
                                            role="tablist"
                                            aria-label={t(`${prefix}.pageTabs.specific`)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.BULK_EMAIL} disabled={isAnyTabLoading}>
                                                    <FiMail size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulkEmail`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey={TAB_KEYS.BULK_SMS} disabled={isAnyTabLoading}>
                                                    <FiMessageSquare size={15} aria-hidden />
                                                    {t(`${prefix}.pageTabs.bulkSms`)}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                        <Tab.Content>
                                            <Tab.Pane eventKey={TAB_KEYS.BULK_EMAIL}>{renderBulkEmailForm()}</Tab.Pane>
                                            <Tab.Pane eventKey={TAB_KEYS.BULK_SMS}>{renderBulkSmsForm()}</Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </Tab.Pane>

                    {canBulkDeleteCheckForecast && (
                        <Tab.Pane eventKey={MAIN_TAB_KEYS.CHECK_FORECAST_DELETE}>
                            <CheckForecastBulkDeletePanel
                                activeSubTab={deleteSubTab}
                                onSubTabChange={setDeleteSubTab}
                                disabled={isAnyTabLoading}
                            />
                        </Tab.Pane>
                    )}
                </Tab.Content>
            </Tab.Container>

            {mainTab !== MAIN_TAB_KEYS.CHECK_FORECAST_DELETE
                && activeTab !== TAB_KEYS.IMPORT_EXCEL
                && activeTab !== TAB_KEYS.STANDARD_IMPORT_EXCEL
                && !isStandardNotifySmsPlaceholder
                && (
                <div className="fb-results">
                    {renderResult()}
                </div>
            )}
        </div>
    );
};

export default AdminTripsForecastBackupCheck;
